import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Download, Loader2 } from "lucide-react";
import type { Attachment } from "../../types";
import { getAccessToken } from "../../lib/supabase";

interface Props {
  attachment: Attachment | null;
  onClose: () => void;
}

export function AttachmentViewer({ attachment, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!attachment) return;
    setLoading(true);
    setError(null);
    setUrl(null);

    const base = (import.meta.env.VITE_API_URL as string | undefined) || "";

    getAccessToken().then((token) => {
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      fetch(`${base}/api/attachments/${attachment.id}`, { headers })
        .then(async (r) => {
          if (!r.ok) throw new Error("erro ao carregar");
          const blob = await r.blob();
          setUrl(URL.createObjectURL(blob));
        })
        .catch(() => setError("Nao foi possivel carregar o anexo"))
        .finally(() => setLoading(false));
    });
  }, [attachment?.id]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (!attachment) return null;

  const type = attachment.contentType || "";
  const isImage = type.startsWith("image/");
  const isVideo = type.startsWith("video/");
  const isAudio = type.startsWith("audio/");
  const isPdf = type === "application/pdf";
  const isText = type.startsWith("text/");
  const isDocx = type.includes("wordprocessingml.document");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="relative flex max-h-[90vh] max-w-[90vw] flex-col overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center gap-3 border-b border-white/5 px-4 py-3">
          <span className="truncate text-sm font-medium text-slate-100">{attachment.name}</span>
          <span className="text-[11px] text-slate-500">
            {(attachment.size / 1024).toFixed(1)} KB
          </span>
          <div className="ml-auto flex items-center gap-1">
            {url && (
              <a
                href={url}
                download={attachment.name}
                className="rounded p-1.5 text-slate-400 hover:bg-white/5 hover:text-slate-200"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </a>
            )}
            <button
              onClick={onClose}
              className="rounded p-1.5 text-slate-400 hover:bg-white/5 hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* conteudo */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
          {error && (
            <p className="p-8 text-center text-sm text-slate-400">{error}</p>
          )}
          {url && isImage && (
            <img src={url} alt={attachment.name} className="max-h-[80vh] object-contain" />
          )}
          {url && isVideo && (
            <video src={url} controls className="max-h-[80vh]" autoPlay />
          )}
          {url && isAudio && (
            <div className="p-6">
              <audio src={url} controls className="w-full" autoPlay />
            </div>
          )}
          {url && isPdf && (
            <iframe src={url} className="h-[80vh] w-[80vw]" title={attachment.name} />
          )}
          {url && isText && (
            <iframe src={url} className="h-[80vh] w-[80vw] border-0" title={attachment.name} />
          )}
          {url && isDocx && (
            <div className="flex flex-col items-center gap-3 p-12">
              <p className="text-sm text-slate-400">Documentos Word nao podem ser visualizados diretamente.</p>
              <a
                href={url}
                download={attachment.name}
                className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 transition"
              >
                Baixar arquivo
              </a>
            </div>
          )}
          {url && !isImage && !isVideo && !isAudio && !isPdf && !isText && !isDocx && (
            <div className="flex flex-col items-center gap-3 p-12">
              <p className="text-sm text-slate-400">Preview nao disponivel para este tipo de arquivo.</p>
              <a
                href={url}
                download={attachment.name}
                className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 transition"
              >
                Baixar arquivo
              </a>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
